import React, { useEffect, useState, useRef } from "react";
import {
  getCountryList,
  removeCompanyImage,
  updateCompanyDetails,
  uploadCompanyFile,
} from "../../../../services/CommonService";
import { toast } from "react-toastify";
import Utils from "../../../../utils";
import { COUNTRY_ID, INDUSTRY_LIST } from "../../../../configs/AppConfig";
import { CustomInput, CustomSelect } from "../../../../components";

const CompanyDetails = (props) => {
  const [countryList, setCountryList] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [numberOfEmployee, setNumberOfEmployee] = useState("");
  const [website, setWebsite] = useState("");
  const [typeOfIndustry, setTypeOfIndustry] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState(0);
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [keyFile, setKeyFile] = useState(Date.now());
  const [isImage, setIsImage] = useState(false);
  const [isViewReadOnly, setIsViewReadOnly] = useState(false);

  const companyProfileModalRef = useRef(null);

  useEffect(() => {
    const data = props?.companyData || {};
    setCompanyName(data.company_name || "");
    setNumberOfEmployee(data.no_employee || "");
    setTypeOfIndustry(data.industry_type || "");
    setWebsite(data.website || "");
    setAddress1(data.address_1 || "");
    setAddress2(data.address_2 || "");
    setCity(data.city || "");
    setState(data.state || "");
    setZip(data.zip_code || "");
    setCountry(data.country_id ?? COUNTRY_ID);
    setCompanyLogo(data.company_logo || "");
    setIsImage(Boolean(data.company_logo));
  }, [props?.companyData]);

  useEffect(() => {
    getCountryList()
      .then((res) => setCountryList(res.data.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let loginData = Utils.loginUserData();
    if (loginData && loginData.role_id > 3) {
      setIsViewReadOnly(true);
    }
  }, []);

  const errorsObj = {
    company_name: "",
    number_of_employee: "",
    website: "",
    type_of_industry: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  };

  const [errors, setErrors] = useState(errorsObj);

  const handleCompanyFile = (e) => {
    if (!e.target.files.length) return;

    const file = e.target.files[0];
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const allowedExtensions = [".jpg", ".jpeg", ".png"];
    const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

    if (!allowedTypes.includes(file.type) || !allowedExtensions.includes(fileExtension)) {
      toast.error("Only JPG, JPEG, and PNG formats are allowed");
      e.target.value = "";
      return;
    }

    props.setLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    uploadCompanyFile(formData)
      .then((response) => {
        let cmpData = { ...props.companyData };
        if (response.data.logo) cmpData.company_logo = response.data.logo;
        props.setCompanyData(cmpData);
        props.setLoading(false);
        setKeyFile(Date.now());
        toast.success(response.data.message);
      })
      .catch((err) => {
        props.setLoading(false);
        setKeyFile(Date.now());
        toast.error(Utils.getErrorMessage(err));
      });
  };

  const onUpdateCompanyDetails = (e) => {
    e.preventDefault();

    let error = false;
    const errorObj = { ...errorsObj };

    if (!companyName.trim()) {
      errorObj.company_name = "Company name is required";
      error = true;
    }
    if (!website.trim()) {
      errorObj.website = "Website is required";
      error = true;
    }
    if (!city.trim()) {
      errorObj.city = "City is required";
      error = true;
    }
    if (!state.trim()) {
      errorObj.state = "State is required";
      error = true;
    }
    if (!zip.trim()) {
      errorObj.zip = "Zip is required";
      error = true;
    }
    if (!address1.trim()) {
      errorObj.address1 = "Address is required";
      error = true;
    }
    if (!country) {
      errorObj.country = "Please select country";
      error = true;
    }

    setErrors(errorObj);
    if (error) return;

    const payload = {
      company_name: companyName,
      no_employee: numberOfEmployee,
      industry_type: typeOfIndustry,
      website,
      address_1: address1,
      address_2: address2,
      city,
      state,
      country_id: country,
      zip,
    };

    props.setLoading(true);
    updateCompanyDetails(payload)
      .then((response) => {
        let cmpData = { ...props.companyData };
        Object.assign(cmpData, payload, { zip_code: zip });
        props.setCompanyData(cmpData);
        props.setLoading(false);
        toast.success(response.data.message);
      })
      .catch((err) => {
        props.setLoading(false);
        toast.error(Utils.getErrorMessage(err));
      });
  };

  const handleRemoveImage = () => {
    props.setLoading(true);

    removeCompanyImage({})
      .then((response) => {
        let cmpData = { ...props.companyData };
        cmpData.company_logo = "";
        props.setCompanyData(cmpData);
        props.setLoading(false);
        setIsImage(false);
        companyProfileModalRef.current?.click();
        toast.success(response.data.message);
      })
      .catch((err) => {
        props.setLoading(false);
        toast.error(Utils.getErrorMessage(err));
      });
  };

  const industryOptions = INDUSTRY_LIST.map((i) => ({ value: i, label: i }));
  const countryOptions = countryList.map((c) => ({ value: c.id, label: c.name }));

  return (
    <div className="setting-details-wrapper">
      <div className="setting-details-container">

        <div className="setting-form-container">
          <form onSubmit={onUpdateCompanyDetails} className="setting-form">

            {/* Basic Information */}
            <div className="setting-form-section">
              <h3 className="setting-section-title">Basic Information</h3>

              <div className="setting-form-grid">
                <div className="setting-form-group">
                  <CustomInput
                    label="Company Name"
                    name="company_name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter Company Name"
                    required
                    error={errors.company_name}
                    disabled={isViewReadOnly}
                  />
                </div>

                <div className="setting-form-group">
                  <CustomInput
                    label="Number of Employees"
                    name="no_employee"
                    value={numberOfEmployee}
                    onChange={(e) => setNumberOfEmployee(e.target.value)}
                    placeholder="Enter Number of Employees"
                    error={errors.number_of_employee}
                    disabled={isViewReadOnly}
                  />
                </div>

                <div className="setting-form-group">
                  <CustomSelect
                    label="Industry Type"
                    name="industry_type"
                    value={typeOfIndustry}
                    options={industryOptions}
                    onChange={(e) => setTypeOfIndustry(e.target.value)}
                    placeholder="Select Industry"
                    error={errors.type_of_industry}
                    isDisabled={isViewReadOnly}
                  />
                </div>

                <div className="setting-form-group">
                  <CustomInput
                    label="Website"
                    name="website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="Enter Website Address"
                    required
                    error={errors.website}
                    disabled={isViewReadOnly}
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="setting-form-section">
              <h3 className="setting-section-title">Address Details</h3>

              <div className="setting-form-grid">
                <div className="setting-form-group full-width">
                  <CustomInput
                    label="Address Line 1"
                    name="address1"
                    value={address1}
                    onChange={(e) => setAddress1(e.target.value)}
                    placeholder="Enter Address Line 1"
                    required
                    error={errors.address1}
                    disabled={isViewReadOnly}
                  />
                </div>

                <div className="setting-form-group full-width">
                  <CustomInput
                    label="Address Line 2"
                    name="address2"
                    value={address2}
                    onChange={(e) => setAddress2(e.target.value)}
                    placeholder="Enter Address Line 2"
                    disabled={isViewReadOnly}
                  />
                </div>

                <div className="setting-form-group">
                  <CustomInput
                    label="City"
                    name="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Enter City"
                    required
                    error={errors.city}
                    disabled={isViewReadOnly}
                  />
                </div>

                <div className="setting-form-group">
                  <CustomInput
                    label="State"
                    name="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="Enter State"
                    required
                    error={errors.state}
                    disabled={isViewReadOnly}
                  />
                </div>

                <div className="setting-form-group">
                  <CustomInput
                    label="Zip Code"
                    name="zip"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    placeholder="Enter Zip"
                    required
                    error={errors.zip}
                    disabled={isViewReadOnly}
                  />
                </div>

                <div className="setting-form-group">
                  <CustomSelect
                    label="Country"
                    name="country"
                    value={country}
                    options={countryOptions}
                    onChange={(e) => setCountry(Number(e.target.value))}
                    placeholder="Select Country"
                    error={errors.country}
                    isDisabled={isViewReadOnly}
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            {!isViewReadOnly && (
              <div className="setting-form-actions">
                <button type="submit" className="setting-btn-primary">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M17 21v-8H7v8M7 3v5h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Save
                </button>
              </div>
            )}
          </form>
        </div>

        <div className="setting-header">
          <div className="setting-header-content">
            <h2 className="setting-header-title">Company Information</h2>
            <p className="setting-header-subtitle">
              Manage your company details and profile
            </p>
          </div>

          <div className="setting-logo-section">
            <div className="setting-logo-container">
              {companyLogo ? (
                <img src={companyLogo} className="setting-logo-image" alt="logo" />
              ) : (
                <div className="setting-logo-placeholder"> <svg width="48" height="48" viewBox="0 0 24 24" fill="none"> <path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5zm10 14H4V9h16v10z" fill="currentColor" /> </svg> </div>
              )}
            </div>

            {!isViewReadOnly && (
              <div className="setting-logo-actions">
                <input
                  type="file"
                  id="setting-logo-upload"
                  key={keyFile}
                  className="setting-file-input-hidden"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleCompanyFile}
                />

                {!isImage ? (
                  <label htmlFor="setting-logo-upload" className="setting-btn-upload">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"> <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> </svg>
                    Upload Logo
                  </label>
                ) : (
                  <button
                    className="setting-btn-remove"
                    data-bs-toggle="modal"
                    data-bs-target="#settingDeleteLogo"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"> <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> </svg>
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ------------------------------- DELETE MODAL ------------------------------- */}
      <div className="modal fade setting-modal" id="settingDeleteLogo" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="setting-modal-title">Delete Company Logo</h5>
              <button
                className="btn-close"
                ref={companyProfileModalRef}
                data-bs-dismiss="modal"
              ></button>
            </div>

            <div className="modal-body">
              <p>Are you sure you want to delete the company logo?</p>
            </div>

            <div className="modal-footer">
              <button className="setting-btn-secondary" data-bs-dismiss="modal">
                Cancel
              </button>

              <button className="setting-btn-danger" onClick={handleRemoveImage}>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetails;
